import {
  Button,
  ContentCard,
  SearchInput,
  SearchProductCard,
  Spinner,
  Tag,
} from '@dfh-corp-sites/ui'
import { cn } from '@dfh-corp-sites/ui/utils'
import { useLocation } from '@gatsbyjs/reach-router'
import { ChevronRight } from '@material-ui/icons'
import React, { useCallback, useEffect, useRef, useState } from 'react'

import { PharmacyResourceType } from '../../../../graphql/api'
import {
  PredictiveSearchDocument,
  PredictiveSearchQuery,
  PredictiveSearchQueryVariables,
} from '../../../../graphql/search'
import useLogAndCaptureError from '../../../../hooks/useLogAndCaptureError'
import useMediaQuery from '../../../../hooks/useMediaQuery'
import { usePrevious } from '../../../../hooks/usePrevious'
import { setAnalyticsEvent } from '../../../../lib/analytics/state'
import { setShowSearch, useShowSearch } from '../../../../lib/header/state'
import { ResourceTypeSlug } from '../../../../lib/library/types'
import { usePageUrl } from '../../../../lib/pages'
import { productCardImageSourceProps } from '../../../../lib/products/card'
import { tryParsingSanityImageJson } from '../../../../lib/sanity'
import { useSearchLazyQuery } from '../../../../lib/search'
import { resourceTypeUrlMapping } from '../../../../utils/libraryTools'
import ResourceLink from '../../../sanity-page/library/ResourceLink'
import NoResults from '../../../shop/products/NoResults'
import TopSearchesList from '../../../shop/products/TopSearches'
import Link from '../../Link'

export interface SearchContainerRef {
  focus(): void
}

export interface SearchContainerProps {
  onSearchClose?: () => void
}

const pushAnalyticsEvents = ({ data, query }: { data: PredictiveSearchQuery; query: string }) => {
  const { libraryResources, pages, parentProducts } = data.predictiveSearch
  const isEmpty = !libraryResources.length && !parentProducts.length && !pages.length
  if (isEmpty) {
    setAnalyticsEvent({
      event: 'search_no_results',
      search_term: query,
    })
  } else {
    //Todo: Define the new analytics event
    setAnalyticsEvent({
      event: 'search',
      search_term: query,
      result_count: libraryResources.length + parentProducts.length + pages.length,
    })
  }
}

const searchDefaultResults: PredictiveSearchQuery['predictiveSearch'] = {
  parentProducts: [],
  libraryResources: [],
  pages: [],
  aggregations: {
    parentProductCategories: [],
  },
  searchTerms: [],
}

const ProductCard: React.FC<{
  product: PredictiveSearchQuery['predictiveSearch']['parentProducts'][0]
}> = ({ product }) => {
  const sanityImageSource = product.mainImage?.json
    ? tryParsingSanityImageJson(product.mainImage.json)
    : undefined

  return (
    <SearchProductCard
      key={product.id}
      tag={product.tagCategories?.[0]}
      image={{
        ...productCardImageSourceProps(sanityImageSource),
        loading: 'lazy',
        alt: product.name,
      }}
      title={<Link to={`/products/${product.urlKey}`}>{product.name}</Link>}
    />
  )
}

const termsAndCategoriesMaxDisplay = 5

const CategoriesAndSearchTermsList: React.FC<{
  searchTerms: PredictiveSearchQuery['predictiveSearch']['searchTerms']
  categories: PredictiveSearchQuery['predictiveSearch']['aggregations']['parentProductCategories']
}> = ({ searchTerms, categories }) => {
  return (
    <div className="space-y-5 divide-y divide-brand-rule-grey">
      <section className="pt-5 xl:pt-0">
        <h3 className="text-sm text-brand-navy uppercase font-medium mb-2.5">Categories</h3>
        {categories.length ? (
          <ul className="flex flex-wrap gap-3 md:gap-5">
            {categories.slice(0, termsAndCategoriesMaxDisplay).map((category) => (
              <li key={category.value}>
                <Link
                  to={`/products?category=${encodeURIComponent(category.value)}`}
                  onClick={() => setShowSearch(false)}
                >
                  <Tag size="lg" variant="category">
                    {category.value}
                  </Tag>
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-gray-500 my-6">No categories found</p>
        )}
      </section>
      <section className="pt-5">
        <h3 className="text-sm text-brand-navy uppercase font-medium mb-2.5">Search Terms</h3>
        {searchTerms?.length ? (
          <ul className="flex flex-wrap gap-3 md:gap-5">
            {searchTerms.slice(0, termsAndCategoriesMaxDisplay).map((searchTerm) => (
              <li key={searchTerm}>
                <Link to={`/search-results?keywords=${encodeURIComponent(searchTerm ?? '')}`}>
                  <Tag size="lg" variant="category">
                    {searchTerm}
                  </Tag>
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-gray-500 my-6">No search terms found</p>
        )}
      </section>
    </div>
  )
}

const SearchResults: React.FC<{
  loading: boolean
  results: PredictiveSearchQuery['predictiveSearch']
}> = ({ loading, results }) => {
  const isDesktop = useMediaQuery({ minWidth: '1280px' })
  const isEmpty =
    !results?.libraryResources.length && !results?.parentProducts.length && !results.pages.length

  if (loading) {
    return (
      <div className="py-16 lg:py-28 flex flex-col gap-3.5 items-center">
        <Spinner />
        <p className="uppercase text-gray-500 text-xs font-normal">Loading Your Results</p>
      </div>
    )
  }

  if (isEmpty) {
    return (
      <div className="h-[calc(100vh-314px)] md:h-[calc(100vh-370px)] lg:h-auto lg:max-h-[calc(100vh-340px)] overflow-auto">
        <NoResults />
      </div>
    )
  }

  return (
    <div className="flex flex-col xl:flex-row xl:gap-5">
      {isDesktop && (
        <div className="xl:w-2/5">
          <CategoriesAndSearchTermsList
            categories={results.aggregations.parentProductCategories}
            searchTerms={results.searchTerms}
          />
        </div>
      )}

      {/* View port height minus the distance between the results and the top to handle scroll */}
      <div className="h-[calc(100vh-314px)] md:h-[calc(100vh-370px)] lg:h-auto lg:max-h-[calc(100vh-340px)] xl:w-3/5 overflow-auto space-y-5 divide-y divide-brand-rule-grey">
        {!isDesktop && (
          <CategoriesAndSearchTermsList
            categories={results.aggregations.parentProductCategories}
            searchTerms={results.searchTerms}
          />
        )}

        {results?.parentProducts.length > 0 && (
          <section className="pt-5 xl:pt-0">
            <header className="flex justify-between items-center mb-2.5 md:mb-5">
              <h3 className="text-sm text-brand-navy uppercase font-medium mb-2.5">Products</h3>
            </header>
            <div className="grid grid-cols-[repeat(auto-fill,minmax(172px,1fr))] md:grid-cols-[repeat(auto-fill,minmax(200px,210px))] gap-2.5 md:gap-5">
              {results?.parentProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          </section>
        )}

        {results.libraryResources.length > 0 && (
          <section className="pt-5">
            <header className="flex justify-between items-center mb-2.5 md:mb-5">
              <h3 className="text-sm text-brand-navy uppercase font-medium mb-2.5">
                Research & Education
              </h3>
            </header>
            <div className="flex flex-wrap md:flex-nowrap gap-2.5 md:gap-5 overflow-auto">
              {results.libraryResources.slice(0, 2).map((resource) => (
                <ResourceLink
                  className="flex-1"
                  slug={resource.slug}
                  title={resource.title}
                  shortDescription={resource.shortDescription}
                  thumbnailImage={resource.thumbnailImage}
                  key={resource.id}
                  category={resource.category}
                  resourceType={
                    resourceTypeUrlMapping[
                      resource.categorySlug as ResourceTypeSlug
                    ] as PharmacyResourceType
                  }
                />
              ))}
            </div>
          </section>
        )}

        {results.pages.length > 0 && (
          <section className="pt-5">
            <header className="flex justify-between items-center mb-2.5 md:mb-5">
              <h3 className="text-sm text-brand-navy uppercase font-medium mb-2.5">Pages</h3>
            </header>
            <div className="flex flex-wrap md:flex-nowrap gap-2.5 md:gap-5 overflow-auto">
              {results.pages.slice(0, 2).map((page) => (
                <ContentCard
                  className="flex-1"
                  key={page.id}
                  title={page.title}
                  description={page.description}
                  cta={
                    <Link to={`/${page.slug}`}>
                      See full details <ChevronRight />
                    </Link>
                  }
                />
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  )
}

// TODO: Ensure case-insensitive search
// TODO: Only search once N characters are entered -- need to confirm the value of N, though
export const SearchContainer = React.memo(
  React.forwardRef<SearchContainerRef, SearchContainerProps>(function SearchContainer(
    { onSearchClose },
    _ref,
  ) {
    const showSearch = useShowSearch()
    const containerRef = useRef<HTMLDivElement>(null)
    const [inputValue, setInputValue] = useState('')
    const keywords = (inputValue || '').trim()
    const [executeAutoCompleteSearch, { data, loading, error, refetch, called }] =
      useSearchLazyQuery(PredictiveSearchDocument, {
        notifyOnNetworkStatusChange: true,
        onCompleted: (searchData) => {
          pushAnalyticsEvents({ data: searchData, query: keywords })
        },
      })
    const { navigate } = usePageUrl()
    useLogAndCaptureError(error)

    const [results, setResults] = useState<PredictiveSearchQuery['predictiveSearch']>(
      data?.predictiveSearch ?? searchDefaultResults,
    )

    const location = useLocation()
    const prevPathName = usePrevious(location.pathname)

    const closeSearch = useCallback(() => {
      setShowSearch(false)
      onSearchClose?.()
    }, [onSearchClose])

    useEffect(() => {
      if (prevPathName && prevPathName !== location.pathname) {
        closeSearch()
      }
    }, [location.pathname, closeSearch, prevPathName])

    useEffect(() => {
      if (loading) {
        return
      }
      setResults(data?.predictiveSearch ?? searchDefaultResults)
    }, [data?.predictiveSearch, loading])

    useEffect(() => {
      if (keywords) {
        const apiVariables: PredictiveSearchQueryVariables = {
          query: keywords,
        }
        if (called && refetch) {
          refetch(apiVariables)
        } else {
          executeAutoCompleteSearch({ variables: apiVariables })
        }
      }
    }, [called, executeAutoCompleteSearch, refetch, keywords])

    const closeOnEscape = useCallback(
      (event: { key: string }) => {
        if (event.key === 'Escape') {
          closeSearch()
        }
      },
      [closeSearch],
    )

    useEffect(() => {
      document.addEventListener('keyup', closeOnEscape)

      return () => {
        document.removeEventListener('keyup', closeOnEscape)
      }
    }, [closeOnEscape])

    const closeOnMouseOutTimeoutRef = useRef<NodeJS.Timeout>()
    const setCloseSearchTimeout = useCallback(() => {
      if (!keywords) {
        closeOnMouseOutTimeoutRef.current = setTimeout(closeSearch, 1000)
      }
    }, [closeSearch, keywords])

    const clearCloseSearchTimeout = useCallback(() => {
      if (closeOnMouseOutTimeoutRef.current) {
        clearTimeout(closeOnMouseOutTimeoutRef.current)
      }
    }, [])

    const handleKeyUp = useCallback(
      (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter' && inputValue.trim().length > 0) {
          closeSearch()
          navigate(`/search-results?keywords=${encodeURIComponent(keywords)}`)
        }
      },
      [closeSearch, inputValue, keywords, navigate],
    )

    useEffect(() => {
      if (showSearch) {
        document.body.style.overflow = 'hidden'
      } else {
        document.body.style.overflow = ''
      }

      return () => {
        document.body.style.overflow = ''
      }
    }, [showSearch])

    useEffect(() => {
      if (!showSearch) return
      const handleClickOutside = (event: MouseEvent) => {
        if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
          closeSearch()
        }
      }

      document.addEventListener('mousedown', handleClickOutside)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }, [showSearch, closeSearch])

    if (!showSearch) return null

    return (
      <>
        <div
          className={cn('fixed inset-x-0 z-10 bg-black/40 pointer-events-none h-screen w-screen')}
        />
        <div
          ref={containerRef}
          onKeyUp={closeOnEscape}
          onMouseEnter={clearCloseSearchTimeout}
          onMouseLeave={setCloseSearchTimeout}
          className="w-full pb-5 md:pb-10 xl:pb-14 pt-6 md:pt-5 xl:pt-10 absolute left-0 top-[109px] md:top-[125px] lg:top-[140px] bg-white z-20 font-gotham shadow-[0px_12px_65px_0px_rgba(0,0,0,0.60)]"
        >
          <div role="search" aria-label="Sitewide">
            <SearchInput
              autoFocus
              placeholder="What can we help you find?"
              containerClassName="mx-5 md:mx-10 xl:mx-auto xl:w-[70%]"
              value={inputValue}
              onChange={(event) => setInputValue(event.target.value)}
              onKeyUp={handleKeyUp}
            />

            <section className="mt-10 xl:mt-[60px] px-2.5 md:px-10 xl:px-20 divide-y divide-brand-rule-grey">
              {keywords ? (
                <div className="xl:w-11/12 mx-auto">
                  <header className="flex justify-between xl:gap-5 items-center xl:mb-5 border-b border-brand-rule-grey xl:border-0">
                    <h2 className="text-2xl md:text-base font-bold md:font-medium text-brand-navy xl:border-b xl:border-brand-rule-grey xl:w-2/5 pb-5">
                      Results
                    </h2>

                    <div className="xl:border-b xl:border-brand-rule-grey xl:w-3/5 pb-5 flex justify-end items-center">
                      <Button variant="text" size="sm" asChild>
                        <Link to={`/search-results?keywords=${encodeURIComponent(keywords)}`}>
                          See all results <ChevronRight />
                        </Link>
                      </Button>
                    </div>
                  </header>
                  <SearchResults loading={loading} results={results} />
                </div>
              ) : (
                <>
                  <h2 className="text-brand-navy text-2xl md:text-3xl xl:text-[40px] font-bold mb-5 xl:mb-8">
                    Top searches
                  </h2>
                  <TopSearchesList className="pt-5 md:pt-8 " />
                </>
              )}
            </section>
          </div>
        </div>
      </>
    )
  }),
)

SearchContainer.displayName = 'SearchContainer'
